<template>
  <div :class="DS_DATEPICKER_CLASSES.Header">
    <DSButton
      data-cy="ds-datepicker__previous-year-button"
      variant="ghost"
      size="md"
      prefix-icon="double-arrow-left"
      @click="updateMonthYear(0, -1)"
    />

    <DSButton
      data-cy="ds-datepicker__previous-month-button"
      variant="ghost"
      prefix-icon="arrow-left"
      @click="updateMonthYear(-1, 0)"
    />

    <DSButton
      ref="monthButtonRef"
      data-cy="ds-datepicker__month-selection-button"
      variant="ghost"
      size="md"
      :aria-expanded="pickerMode === DS_DATEPICKER_CALENDAR_MODES.Month"
      @click="toggleMonth"
    >
      {{ props?.months?.find(m => m.value === props.month)?.text || "" }}
    </DSButton>

    <DSButton
      ref="yearButtonRef"
      data-cy="ds-datepicker__year-selection-button"
      variant="ghost"
      size="md"
      :aria-expanded="pickerMode === DS_DATEPICKER_CALENDAR_MODES.Year"
      @click="toggleYear"
    >
      {{ props.year }}
    </DSButton>

    <DSButton
      data-cy="ds-datepicker__next-month-button"
      variant="ghost"
      size="md"
      prefix-icon="arrow-right"
      @click="updateMonthYear(1, 0)"
    />

    <DSButton
      data-cy="ds-datepicker__next-year-button"
      variant="ghost"
      size="md"
      prefix-icon="double-arrow-right"
      @click="updateMonthYear(0, 1)"
    />
  </div>

  <DSDatePickerPeriodSelector
    v-if="pickerMode === DS_DATEPICKER_CALENDAR_MODES.Month && props.months"
    :type="DS_DATEPICKER_CALENDAR_MODES.Month"
    :selected-value="props.month"
    :options="props.months"
    @select:option="selectMonthOrYear($event, props.year)"
  />

  <DSDatePickerPeriodSelector
    v-if="pickerMode === DS_DATEPICKER_CALENDAR_MODES.Year && props.years"
    :type="DS_DATEPICKER_CALENDAR_MODES.Year"
    :selected-value="props.year"
    :options="props.years"
    @select:option="selectMonthOrYear(props?.month || 0, $event)"
  />
</template>

<script setup lang="ts">
import { ref } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSDatePickerPeriodSelector from "@/src/design_system/form_elements/datepicker/components/DSDatePickerPeriodSelector.vue";
import { DSDatePickerHeaderProps } from "@/src/design_system/form_elements/datepicker/types/header";
import { DS_DATEPICKER_CLASSES } from "@/src/design_system/form_elements/datepicker/constants/classes";
import {
  DS_DATEPICKER_CALENDAR_MODES,
  DSDatepickerCalendarMode,
} from "@/src/design_system/form_elements/datepicker/types/calendar";

const props = withDefaults(defineProps<DSDatePickerHeaderProps>(), {
  months: undefined,
  years: undefined,
  updateMonthYear: undefined,
});

const monthButtonRef = ref<InstanceType<typeof DSButton>>();
const yearButtonRef = ref<InstanceType<typeof DSButton>>();

const toggleMonth = () => {
  if (pickerMode.value === DS_DATEPICKER_CALENDAR_MODES.Month) {
    pickerMode.value = DS_DATEPICKER_CALENDAR_MODES.Date;
    return;
  }
  pickerMode.value = DS_DATEPICKER_CALENDAR_MODES.Month;
};

const toggleYear = () => {
  if (pickerMode.value === DS_DATEPICKER_CALENDAR_MODES.Year) {
    pickerMode.value = DS_DATEPICKER_CALENDAR_MODES.Date;
    return;
  }
  pickerMode.value = DS_DATEPICKER_CALENDAR_MODES.Year;
};

const pickerMode = defineModel("pickerMode", {
  type: String as () => DSDatepickerCalendarMode,
  required: true,
});

const setPickerMode = (mode: DSDatepickerCalendarMode) => {
  pickerMode.value = mode;
};

const selectMonthOrYear = (month: number, year: number) => {
  props.updateMonthYear?.(month, year, false);
  // Chromium blurs the removed option button without a related target, which the picker boundary reads as leaving.
  const toggleButton = pickerMode.value === DS_DATEPICKER_CALENDAR_MODES.Month ? monthButtonRef : yearButtonRef;
  (toggleButton.value?.$el as HTMLElement | undefined)?.focus();
  setPickerMode(DS_DATEPICKER_CALENDAR_MODES.Date);
};

const updateMonthYear = (monthIncrement: number, yearIncrement: number) => {
  let newMonth = props.month + monthIncrement;
  let newYear = props.year + yearIncrement;

  if (newMonth > 11) {
    newMonth = 0;
    newYear += 1;
  } else if (newMonth < 0) {
    newMonth = 11;
    newYear -= 1;
  }

  props.updateMonthYear?.(newMonth, newYear, true);
};
</script>
